/**
 * Agents Catalogue Service
 *
 * Fetches the list of active agents from the backend's `GET /agents`
 * endpoint. The list replaces the previously hardcoded AGENT_REGISTRY in
 * src/config/agent-config.ts.
 *
 * Returns the tuple pattern [Error | null, Data | null] used throughout
 * the services layer.
 */

import { appConfig } from '@/config/app-config';
import { apiRoutes } from '@/config/api-routes';
import type { AgentConfig } from '@/config/agent-config';
import { tokenStorage } from '@/utils/storage-keys';

interface BackendAgentResponse {
  id: string;
  name: string;
  description?: string | null;
  frontend_config?: {
    icon?: string | null;
    title?: string | null;
    welcomeMessage?: string | null;
    welcomeDescription?: string | null;
    inputPlaceholder?: string | null;
    gettingStartedHint?: string | null;
  } | null;
}

interface BackendAgentCatalogueResponse {
  agents: BackendAgentResponse[];
}

/**
 * Map a backend row into the FE's AgentConfig. Falls back to sensible
 * defaults when frontend_config is null/partial — that way a new agent
 * created in DB without FE config still shows up in the picker (with
 * generic copy) instead of breaking the renderer.
 */
function toAgentConfig(row: BackendAgentResponse): AgentConfig {
  const fc = row.frontend_config ?? {};
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? '',
    icon: fc.icon ?? undefined,
    title: fc.title ?? row.name,
    welcomeMessage: fc.welcomeMessage ?? `Welcome to ${row.name}.`,
    welcomeDescription:
      fc.welcomeDescription ?? row.description ?? 'How can I help today?',
    inputPlaceholder: fc.inputPlaceholder ?? 'Type a message...',
    gettingStartedHint: fc.gettingStartedHint ?? undefined,
  };
}

export const agentsService = {
  /**
   * Fetch the active agent catalogue from the backend.
   *
   * Sends the OAuth token as a Bearer header — required by
   * RequireAuthMiddleware, which only allow-lists /health, /info,
   * /oauth/login, and /oauth/callback.
   */
  async listAgents(): Promise<[Error | null, AgentConfig[] | null]> {
    const url = `${appConfig.API_BASE_URL}${apiRoutes.agents}`;
    const token = tokenStorage.get();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        return [
          new Error(
            `GET ${apiRoutes.agents} failed: ${response.status} ${response.statusText}`,
          ),
          null,
        ];
      }

      const body = (await response.json()) as BackendAgentCatalogueResponse;
      if (!Array.isArray(body?.agents)) {
        return [
          new Error(`GET ${apiRoutes.agents}: unexpected response shape`),
          null,
        ];
      }

      const mapped = body.agents.map(toAgentConfig);
      return [null, mapped];
    } catch (error) {
      return [
        error instanceof Error
          ? error
          : new Error(`GET ${apiRoutes.agents} failed: ${String(error)}`),
        null,
      ];
    }
  },
};
